/**
 * Critical Path Positioning
 * 
 * Positions nodes on the main flow (critical path) before feedback loops are added.
 */

import { HORIZONTAL_GAP, VERTICAL_GAP, PADDING, getNodeDimensions } from './constants';

/**
 * Position critical path nodes within their layers
 * 
 * @param {Array} layers - Array of layer arrays, each containing node IDs
 * @param {Object} graph - Graph with parent/child relationships
 * @param {Object} nodeDimensions - Map of node ID to dimensions
 * @returns {{ positions: Object, layerYPositions: Array }} Positioned nodes and layer Y coordinates
 */
export function positionCriticalPath(layers, graph, nodeDimensions) {
  const positions = {};
  const layerYPositions = [];
  let currentY = PADDING;

  layers.forEach((layer, layerIndex) => {
    // Calculate Y position for this layer
    if (layerIndex > 0) {
      const prevLayer = layers[layerIndex - 1];
      const maxPrevHeight = Math.max(
        ...prevLayer.map(id => getNodeDimensions(id, nodeDimensions).height)
      );
      currentY += maxPrevHeight + VERTICAL_GAP;
    }
    
    layerYPositions[layerIndex] = currentY;
    
    // Position nodes in this layer
    if (layerIndex === 0) {
      // First layer: simple left-to-right placement
      positionFirstLayer(layer, positions, currentY, nodeDimensions);
    } else {
      // Subsequent layers: align with parents
      positionSubsequentLayer(layer, positions, currentY, graph, nodeDimensions);
    }
  });

  return { positions, layerYPositions };
}

/**
 * Position nodes in the first layer (no parents to align with)
 */
function positionFirstLayer(layer, positions, currentY, nodeDimensions) {
  let currentX = PADDING;
  
  layer.forEach((taskId, nodeIndex) => {
    if (nodeIndex > 0) {
      const prevTaskId = layer[nodeIndex - 1];
      const prevWidth = getNodeDimensions(prevTaskId, nodeDimensions).width;
      currentX = positions[prevTaskId].x + prevWidth + HORIZONTAL_GAP;
    }
    positions[taskId] = { x: currentX, y: currentY };
  });
}

/**
 * Position nodes in subsequent layers (align with parent centers)
 * 
 * ENHANCED: Groups siblings that share the same parent(s) and centers the
 * group under the parent, rather than centering each child individually.
 * If children are wider than parent position allows, parents are shifted right.
 */
function positionSubsequentLayer(layer, positions, currentY, graph, nodeDimensions) {
  // STEP 1: Group nodes by their parent signature (nodes with identical parents are siblings)
  const siblingGroups = groupSiblings(layer, graph);
  
  // STEP 2: Calculate ideal X for each sibling group (based on parent centers)
  const groupPositions = siblingGroups.map(group => {
    const parentSignature = getParentSignature(group[0], graph);
    const parents = graph.parents.get(group[0]) || [];
    
    // Calculate total width of this sibling group
    let groupWidth = 0;
    group.forEach((taskId, idx) => {
      if (idx > 0) groupWidth += HORIZONTAL_GAP;
      groupWidth += getNodeDimensions(taskId, nodeDimensions).width;
    });
    
    return { 
      group, 
      parents,
      parentSignature, 
      groupWidth, 
    };
  });
  
  // STEP 3: Sort groups by the X position of their parents (leftmost parent first)
  groupPositions.sort((a, b) => {
    const aParentX = a.parents.length > 0 && positions[a.parents[0]] 
      ? positions[a.parents[0]].x 
      : Infinity;
    const bParentX = b.parents.length > 0 && positions[b.parents[0]] 
      ? positions[b.parents[0]].x 
      : Infinity;
    const diff = aParentX - bParentX;
    if (Math.abs(diff) < 0.001) return (a.parentSignature || '').localeCompare(b.parentSignature || '');
    return diff;
  });
  
  // STEP 4: Place groups, shifting parents right if needed to keep children centered
  // Track which nodes have been placed in this layer so far, so we can
  // recalculate rightEdge after shiftParentsRight moves already-placed nodes.
  let rightEdge = 0;
  const placedInThisLayer = [];
  
  groupPositions.forEach(({ group, parents, groupWidth }) => {
    // Calculate the current center point of all parents
    const parentCenters = parents
      .filter(pid => positions[pid])
      .map(pid => {
        const parentWidth = getNodeDimensions(pid, nodeDimensions).width;
        return positions[pid].x + parentWidth / 2;
      });
    
    let parentCenter = PADDING + groupWidth / 2; // Default if no parents
    if (parentCenters.length > 0) {
      parentCenter = parentCenters.reduce((a, b) => a + b, 0) / parentCenters.length;
    }
    
    // Ideal X is where the GROUP should start to center under parent
    let idealGroupX = parentCenter - groupWidth / 2;
    
    // Calculate the minimum allowed start X (respecting padding and previous groups)
    const minGroupX = Math.max(PADDING, rightEdge + HORIZONTAL_GAP);
    
    // If ideal position would violate constraints, we need to shift parents right
    if (idealGroupX < minGroupX) {
      const shiftAmount = minGroupX - idealGroupX;
      
      // Shift all parents (and their ancestors AND already-positioned descendants)
      shiftParentsRight(parents, shiftAmount, positions, graph, nodeDimensions);
      
      // Recalculate rightEdge: shiftParentsRight may have moved nodes that were
      // already placed in this layer (descendants of shifted ancestors)
      rightEdge = recalcRightEdge(placedInThisLayer, positions, nodeDimensions);
      
      // Recalculate parent center after shift
      const newParentCenters = parents
        .filter(pid => positions[pid])
        .map(pid => {
          const parentWidth = getNodeDimensions(pid, nodeDimensions).width;
          return positions[pid].x + parentWidth / 2;
        });
      
      if (newParentCenters.length > 0) {
        parentCenter = newParentCenters.reduce((a, b) => a + b, 0) / newParentCenters.length;
      }
      idealGroupX = parentCenter - groupWidth / 2;
    }
    
    // Now place the group at the ideal position (which should now be valid)
    let groupStartX = Math.max(PADDING, idealGroupX, rightEdge + HORIZONTAL_GAP);
    
    // Place each node in the group left-to-right
    let currentX = groupStartX;
    group.forEach((taskId, idx) => {
      if (idx > 0) currentX += HORIZONTAL_GAP;
      const width = getNodeDimensions(taskId, nodeDimensions).width;
      positions[taskId] = { x: currentX, y: currentY };
      placedInThisLayer.push(taskId);
      currentX += width;
    });
    
    rightEdge = currentX;
  });
}

/**
 * Recalculate the rightEdge from the list of already-placed node IDs.
 * Called after shiftParentsRight may have moved previously placed nodes.
 */
function recalcRightEdge(placedIds, positions, nodeDimensions) {
  let edge = 0;
  for (const taskId of placedIds) {
    const pos = positions[taskId];
    if (pos) {
      const width = getNodeDimensions(taskId, nodeDimensions).width;
      edge = Math.max(edge, pos.x + width);
    }
  }
  return edge;
}

/**
 * Shift a set of parent nodes (and all their ancestors AND already-positioned
 * descendants) to the right.
 * 
 * This is used when children are wider than parents and need more space.
 * 
 * IMPORTANT: We must also shift already-positioned descendants of the shifted
 * nodes. Otherwise, previously placed children end up to the LEFT of their
 * shifted parent, causing a cascading leftward slant in the layout.
 */
function shiftParentsRight(parentIds, shiftAmount, positions, graph, nodeDimensions) {
  if (shiftAmount <= 0) return;
  
  // Collect all ancestors that need to be shifted (go UP the tree)
  const nodesToShift = new Set();
  const ancestorQueue = [...parentIds];
  
  while (ancestorQueue.length > 0) {
    const nodeId = ancestorQueue.shift();
    if (nodesToShift.has(nodeId)) continue;
    if (!positions[nodeId]) continue;
    
    nodesToShift.add(nodeId);
    
    // Add this node's parents to the queue (go up the tree)
    const nodeParents = graph.parents.get(nodeId) || [];
    ancestorQueue.push(...nodeParents);
  }
  
  // Also collect already-positioned descendants of shifted nodes (go DOWN the tree)
  // This prevents children that were already placed from being left behind
  // when their parent shifts right.
  const descendantQueue = [...nodesToShift];
  
  while (descendantQueue.length > 0) {
    const nodeId = descendantQueue.shift();
    const children = graph.children.get(nodeId) || [];
    for (const childId of children) {
      // Only shift children that are already positioned and not yet in our set
      if (!nodesToShift.has(childId) && positions[childId]) {
        nodesToShift.add(childId);
        descendantQueue.push(childId);
      }
    }
  }
  
  // Shift all collected nodes
  for (const nodeId of nodesToShift) {
    if (positions[nodeId]) {
      positions[nodeId] = {
        ...positions[nodeId],
        x: positions[nodeId].x + shiftAmount,
      };
    }
  }
}

/**
 * Group nodes by their parent signature (nodes with the same set of parents)
 * Siblings should stay together as a group
 */
function groupSiblings(layer, graph) {
  const signatureMap = new Map(); // parentSignature -> [taskIds]
  
  layer.forEach(taskId => {
    const signature = getParentSignature(taskId, graph);
    if (!signatureMap.has(signature)) {
      signatureMap.set(signature, []);
    }
    signatureMap.get(signature).push(taskId);
  });
  
  // Sort nodes within each group by ID for determinism
  for (const group of signatureMap.values()) {
    group.sort((a, b) => (a || '').localeCompare(b || ''));
  }
  
  return Array.from(signatureMap.values());
}

/**
 * Create a canonical signature string from a node's parents
 */
function getParentSignature(taskId, graph) {
  const parents = graph.parents.get(taskId) || [];
  return [...parents].sort().join(',') || '__no_parents__';
}

